<template>
  <div class="row" v-if="$gate.isAdmin()">
    <div class="toolbar py-6">
      <div class="container-fluid d-flex align-items-center">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <span class="h-20px border-gray-200 border-start mx-3"></span>
          <ol class="breadcrumb breadcrumb-dot text-muted fs-6 fw-semibold">
            <li class="breadcrumb-item pe-3">
              <a @click.prevent="onBackClick" style="cursor: pointer;" class="pe-3">Înapoi</a>
            </li>
            <li class="breadcrumb-item pe-3 text-muted">Căutare firme</li>
          </ol>
          <span class="h-20px border-gray-200 border-start mx-3"></span>
        </div>
      </div>
    </div>

    <div class="content d-flex flex-column flex-column-fluid">
      <div class="container-fluid">
        <div class="card card-flush">
          <div class="card-header align-items-center py-5 gap-2 gap-md-5">
            <div class="card-title">
              <div class="d-flex align-items-center position-relative my-1">
                <span class="me-5 fs-4 fw-bold">Căutare firme</span>
              </div>
            </div>

            <div class="card-toolbar flex-row-fluid justify-content-end gap-5">
              <!-- Collapsed search button -->
              <button
                v-if="searchCollapsed && selectedFirma"
                type="button"
                class="btn btn-icon btn-light btn-active-light-primary me-3"
                @click="expandSearch"
                :title="'Caută din nou'"
                aria-label="Deschide căutarea"
              >
                <i class="fa-solid fa-magnifying-glass fs-2"></i>
              </button>

              <!-- Full search bar -->
              <div v-else class="smart-search w-100 d-flex justify-content-end">
                <div class="smart-search__box">
                  <i class="smart-search__icon" aria-hidden="true"></i>
                  <input
                    type="text"
                    class="smart-search__input"
                    v-model.trim="searchTerm"
                    placeholder="Caută după user, ID user, ID firmă, denumire sau CUI..."
                    @keydown.enter.prevent="triggerSearch"
                    @keydown.esc.prevent="clearSearch"
                    @input="onSearchInput"
                    @focus="focused = true"
                    @blur="onBlur"
                  />
                  <button
                    type="button"
                    class="smart-search__clear"
                    v-show="searchTerm"
                    @mousedown.prevent
                    @click="clearSearch"
                    aria-label="Șterge căutarea"
                    title="Șterge"
                  >
                    ×
                  </button>
                  <div class="smart-search__spinner" v-show="searching" aria-hidden="true"></div>

                  <!-- Results dropdown -->
                  <div class="smart-results" v-show="focused && (searchResults.length || searchEmpty)">
                    <div v-if="searchResults.length">
                      <div class="smart-results__header">Rezultate ({{ searchResults.length }})</div>
                      <ul class="smart-results__list">
                        <li v-for="r in searchResults" :key="`rs_${r.id}`" class="smart-results__item" @mousedown.prevent @click="openFirma(r)">
                          <div class="d-flex align-items-center w-100">
                            <div class="symbol symbol-circle symbol-50px overflow-hidden me-3 flex-shrink-0">
                              <div v-if="r.sigla" class="symbol-label">
                                <img :src="`/api/fisiere/sigla-firma/${r.id}`" alt="Sigla" style="width: 100%; height: 100%; object-fit: cover;" />
                              </div>
                              <div v-else class="symbol-label fs-6 bg-light-primary text-primary">
                                {{ (r.denumire || '?').charAt(0) }}
                              </div>
                            </div>
                            <div class="flex-grow-1 min-w-0">
                              <div class="text-truncate fw-semibold">{{ r.denumire }} <span class="text-muted">#{{ r.id }}</span></div>
                              <div class="text-muted small text-truncate">CUI: {{ r.cui || '-' }}<span v-if="r.user_name"> · Utilizator: {{ r.user_name }}</span></div>
                            </div>
                            <span class="badge badge-light ms-3">Deschide</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div v-else-if="searchEmpty" class="smart-results__empty">Niciun rezultat</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body pt-0">
            <div v-if="selectedFirma" class="alert alert-primary d-flex align-items-center justify-content-between py-4 px-5">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-circle symbol-50px overflow-hidden me-4">
                  <div v-if="selectedFirma.sigla" class="symbol-label">
                    <img :src="`/api/fisiere/sigla-firma/${selectedFirma.id}`" alt="Sigla" style="width:100%;height:100%;object-fit:cover;" />
                  </div>
                  <div v-else class="symbol-label fs-5 bg-light-primary text-primary">
                    {{ (selectedFirma.denumire || '?').charAt(0) }}
                  </div>
                </div>
                <div>
                  <div class="fw-bold fs-5">Firma selectată: {{ selectedFirma.denumire }} <span class="text-muted">(#{{ selectedFirma.id }})</span></div>
                  <div class="text-muted">CUI: {{ selectedFirma.cui || '-' }}<span v-if="selectedFirma.user_name"> · Utilizator: {{ selectedFirma.user_name }}</span></div>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <button type="button" class="btn btn-sm btn-light" @click="expandSearch"><i class="fa-solid fa-magnifying-glass fs-3"></i></button> 
                <button type="button" class="btn btn-sm btn-light-danger" @click="clearSelection">Șterge selecția</button>
              </div>
            </div>
          </div>

        </div>

        <!-- Second card: Module buttons + dynamic area -->
        <div class="card card-flush mt-6" v-if="selectedFirma || $route.params.firmaId">
          <div class="card-header align-items-center py-5 gap-2 gap-md-5">
            <div class="card-title">
              <div class="d-flex align-items-center position-relative my-1">
                <span class="me-5 fs-4 fw-bold">Module rapide</span>
              </div>
            </div>
          </div>
          <div class="card-body pt-0">
            <!-- Buttons row -->
            <div class="d-flex flex-wrap gap-4" :class="{ 'tiles-compact': compactModules }">
              <button type="button" class="tile-btn" :class="{ 'tile-btn--active': activeModule==='facturare' }" @click="selectModule('facturare')">
                <i class="fa-solid fa-file-invoice"></i>
                <span>Facturare</span>
              </button>
              <button type="button" class="tile-btn" :class="{ 'tile-btn--active': activeModule==='contabilitate' }" @click="selectModule('contabilitate')">
                <i class="fa-solid fa-calculator"></i>
                <span>Contabilitate</span>
              </button>
              <button type="button" class="tile-btn" :class="{ 'tile-btn--active': activeModule==='angajati' }" @click="selectModule('angajati')">
                <i class="fa-solid fa-users"></i>
                <span>Angajați</span>
              </button>
              <button type="button" class="tile-btn" :class="{ 'tile-btn--active': activeModule==='fisiere' }" @click="selectModule('fisiere')">
                <i class="fa-solid fa-folder-open"></i>
                <span>Fișiere</span>
              </button>
            </div>

            <!-- Dynamic area under buttons -->
            <div class="mt-6">
              <div v-if="!activeModule" class="text-muted">Alege un modul pentru a vedea opțiunile.</div>

              <div v-else-if="activeModule==='facturare'">
                <h5 class="mb-4">Facturare</h5>
                <div class="d-flex flex-wrap gap-4 mb-6" :class="{ 'tiles-compact': compactSubModules }">
                  <button type="button" class="tile-btn" :class="{ 'tile-btn--active': subModule==='emise' }" @click="selectSubModule('emise')">
                    <i class="fa-solid fa-file-invoice-dollar"></i>
                    <span>Vezi facturi emise</span>
                  </button>
                  <button type="button" class="tile-btn" :class="{ 'tile-btn--active': subModule==='primite' }" @click="selectSubModule('primite')">
                    <i class="fa-solid fa-file-import"></i>
                    <span>Vezi facturi primite</span>
                  </button>
                  <button
                    type="button"
                    class="tile-btn"
                    :class="{ 'tile-btn--active': subModule==='extrase' }"
                    @click="selectSubModule('extrase')"
                  >
                    <i class="fa-solid fa-receipt"></i>
                    <span>Vezi extrase</span>
                  </button>
                </div>

                <!-- Results -->
                <div v-if="subModule==='emise'">
                  <FacturiEmise :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>

                <div v-else-if="subModule==='primite'">
                  <FacturiPrimite :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>

                <div v-else-if="subModule==='extrase'">
                  <ExtraseBancare :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>
              </div>

              <div v-else-if="activeModule==='contabilitate'">
                <h5 class="mb-4">Contabilitate</h5>
                <div
                  class="d-flex flex-wrap gap-4 mb-6"
                  :class="{ 'tiles-compact': compactSubModules }"
                >
                  <button
                    type="button"
                    class="tile-btn"
                    :class="{ 'tile-btn--active': subModule==='registru_jurnal' }"
                    @click="selectSubModule('registru_jurnal')"
                  >
                    <i class="fa-solid fa-book"></i>
                    <span>Registru jurnal</span>
                  </button>
                  <button
                    type="button"
                    class="tile-btn"
                    :class="{ 'tile-btn--active': subModule==='balanta' }"
                    @click="selectSubModule('balanta')"
                  >
                    <i class="fa-solid fa-scale-balanced"></i>
                    <span>Balanță</span>
                  </button>
                  <button
                    type="button"
                    class="tile-btn"
                    :class="{ 'tile-btn--active': subModule==='raport_tva' }"
                    @click="selectSubModule('raport_tva')"
                  >
                    <i class="fa-solid fa-file-lines"></i>
                    <span>Raport TVA</span>
                  </button>
                </div>

                <!-- Rezultate contabilitate -->
                <div v-if="subModule==='registru_jurnal'">
                  <RegistruJurnal :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>
                <div v-else-if="subModule==='balanta'">
                  <div class="alert alert-info mb-0">
                    Modulul de Balanță urmează să fie implementat.
                  </div>
                </div>
                <div v-else-if="subModule==='raport_tva'">
                  <div class="alert alert-info mb-0">
                    Modulul de Raport TVA urmează să fie implementat.
                  </div>
                </div>
              </div>

              <div v-else-if="activeModule==='angajati'">
                <h5 class="mb-4">Angajați</h5>
                <div class="row g-4">
                  <div class="col-md-4">
                    <div class="p-4 border rounded-3 bg-light">Contracte</div>
                  </div>
                  <div class="col-md-4">
                    <div class="p-4 border rounded-3 bg-light">State de plată</div>
                  </div>
                  <div class="col-md-4">
                    <div class="p-4 border rounded-3 bg-light">Pontaje</div>
                  </div>
                </div>
              </div>
              <div v-else-if="activeModule==='fisiere'">
                <h5 class="mb-4">Fișiere</h5>
                <div class="d-flex flex-wrap gap-4 mb-6" :class="{ 'tiles-compact': compactSubModules }">
                  <button type="button" class="tile-btn" :class="{ 'tile-btn--active': subModule==='iesiri' }" @click="selectSubModule('iesiri')">
                    <i class="fa-solid fa-file-export"></i>
                    <span>Vezi ieșiri</span>
                  </button>
                  <button type="button" class="tile-btn" :class="{ 'tile-btn--active': subModule==='intrari' }" @click="selectSubModule('intrari')">
                    <i class="fa-solid fa-file-import"></i>
                    <span>Vezi intrări</span>
                  </button>
                  <button type="button" class="tile-btn" :class="{ 'tile-btn--active': subModule==='extrase' }" @click="selectSubModule('extrase')">
                    <i class="fa-solid fa-receipt"></i>
                    <span>Vezi extrase</span>
                  </button>
                  <button type="button" class="tile-btn" :class="{ 'tile-btn--active': subModule==='altedocumente' }" @click="selectSubModule('altedocumente')">
                    <i class="fa-solid fa-folder"></i>
                    <span>Vezi alte documente</span>
                  </button>
                  <button type="button" class="tile-btn" :class="{ 'tile-btn--active': subModule==='tot' }" @click="selectSubModule('tot')">
                    <i class="fa-solid fa-layer-group"></i>
                    <span>Vezi tot</span>
                  </button>
                </div>

                <!-- Results area for files submodules (placeholder hooks) -->
                <div v-if="subModule==='iesiri'">
                  <AdminIncarcariIesiri :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>
                <div v-else-if="subModule==='intrari'">
                  <AdminIncarcariIntrari :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>
                <div v-else-if="subModule==='extrase'">
                  <AdminIncarcariExtrase :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>
                <div v-else-if="subModule==='altedocumente'">
                  <AdminIncarcariAlteDoc :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>
                <div v-else-if="subModule==='tot'">
                  <AdminIncarcariToate :firma-id="Number(selectedFirma?.id || $route.params.firmaId)" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FacturiEmise from './work_components/FacturiEmise.vue';
import FacturiPrimite from './work_components/FacturiPrimite.vue';
import ExtraseBancare from './work_components/ExtraseBancare.vue';
import AdminIncarcariIntrari from './work_components/Admin_IncarcariIntrari.vue';
import AdminIncarcariIesiri from './work_components/Admin_IncarcariIesiri.vue';
import AdminIncarcariExtrase from './work_components/Admin_IncarcariExtrase.vue';
import AdminIncarcariAlteDoc from './work_components/Admin_IncarcariAlteDoc.vue';
import AdminIncarcariToate from './work_components/Admin_IncarcariToate.vue';
import RegistruJurnal from './work_components/RegistruJurnal.vue';

export default {
  components: { FacturiEmise, FacturiPrimite, AdminIncarcariIntrari, AdminIncarcariIesiri, AdminIncarcariExtrase, AdminIncarcariAlteDoc, AdminIncarcariToate, ExtraseBancare, RegistruJurnal },
  data () {
    return {
      // doar starea de search
      searchTerm: '',
      searching: false,
      searchResults: [],
      searchEmpty: false,
      showSuggestions: true,
      _searchDebounce: null,
      focused: false,
      _blurTimer: null,
      selectedFirma: null,
      searchCollapsed: false,
      activeModule: null,
      subModule: null,
      compactModules: false,
      compactSubModules: false,
    };
  },
  computed: {
  },
  watch: {
    subModule(newVal, oldVal) {
      // Parent no longer touches child DataTables; components handle their own lifecycle
      if (newVal !== oldVal) {
        this.compactSubModules = true;
      }
    },
    $route: {
      immediate: false,
      handler() {
        this.applyRoute();
      }
    },
  },
  mounted() {
    this.applyRoute();
  },
  beforeUnmount() 
  {

  },
  methods: {
    // --- ROUTE HELPERS ---
    routePathFor(firmaId, module, submodule) {
      const parts = ['/administrare/work'];
      if (firmaId) parts.push(String(firmaId));
      if (module) parts.push(String(module));
      if (submodule) parts.push(String(submodule));
      return parts.join('/');
    },
    navigate(firmaId, module, submodule) {
      const path = this.routePathFor(firmaId, module, submodule);
      if (this.$route && this.$route.path !== path) {
        this.$router.push(path).catch(() => {});
      }
    },
    normalizeFirma(raw) {
      if (!raw || typeof raw !== 'object') return null;
      const id = raw.id || raw.firma_id || raw.ID || null;
      return {
        id,
        denumire: raw.denumire || raw.denumire_firma || raw.nume || raw.name || '',
        cui: raw.cui || raw.cui_firma || raw.cif || raw.vat || '',
        user_name: raw.user_name || raw.user || raw.utilizator || '',
        sigla: raw.sigla || raw.logo || raw.sigla_url || null,
      };
    },
    async fetchFirmaById(id) {
      const safeSet = (obj) => {
        const f = this.normalizeFirma(obj);
        if (f && f.id) {
          this.selectedFirma = f;
          return true;
        }
        return false;
      };
      try {
        // 1) Primary: REST-style endpoint
        const res1 = await axios.get(`/api/administrare/work/firma/${id}`);
        if (res1 && res1.data && (safeSet(res1.data.firma || res1.data))) return;
      } catch (_) { /* fallthrough */ }
      try {
        // 2) Variant: query param
        const res2 = await axios.get('/api/administrare/work/firma', { params: { id } });
        if (res2 && res2.data && (safeSet(res2.data.firma || res2.data))) return;
      } catch (_) { /* fallthrough */ }
      try {
        // 3) Fallback: search API with explicit id: filter
        const res3 = await axios.put('/api/administrare/work/cautare', { q: `id:${id}` });
        const rows = (res3 && res3.data && (res3.data.firme || res3.data)) || [];
        if (Array.isArray(rows) && rows.length > 0 && safeSet(rows[0])) return;
      } catch (_) { /* fallthrough */ }
      // As a last resort keep at least the id so the page works
      this.selectedFirma = { id: Number(id) };
    },
    applyRoute() {
      const params = (this.$route && this.$route.params) || {};
      const firmaId = params.firmaId ? Number(params.firmaId) : null;
      let module = params.module || null;
      let sub = params.submodule || null;

      // Ensure firma is loaded if we have an id in route
      if (firmaId && (!this.selectedFirma || Number(this.selectedFirma.id) !== firmaId)) {
        this.fetchFirmaById(firmaId);
      }

      // Normalize to internal keys used by the UI
      // Route uses: facturare/facturi_emise | facturare/facturi_primite
      if (sub === 'facturi_emise') sub = 'emise';
      if (sub === 'facturi_primite') sub = 'primite';

      // If we have a submodule but no module in URL, default module is facturare
      if (!module && sub) module = 'facturare';

      if (module) {
        this.activeModule = module;
        this.compactModules = true;
      } else {
        this.activeModule = null;
        this.compactModules = false;
      }

      if (sub) {
        this.subModule = sub;
        this.compactSubModules = true;
      } else {
        this.subModule = null;
        this.compactSubModules = false;
      }
    },
    // --- SEARCH ---
    onSearchInput() {
      const term = this.searchTerm || '';
      this.showSuggestions = term.length === 0;
      if (this._searchDebounce) clearTimeout(this._searchDebounce);
      if (term.length < 2) {
        this.searchResults = [];
        this.searchEmpty = false;
        this.searching = false;
        return;
      }
      this._searchDebounce = setTimeout(() => this.triggerSearch(), 350);
    },
    triggerSearch() {
      const term = (this.searchTerm || '').trim();
      if (!term || term.length < 2) return;
      this.searching = true;

      // Controller separat pentru Work.vue
      axios.put('/api/administrare/work/cautare', { q: term })
        .then((res) => {
          const rows = res.data && (res.data.firme || res.data) || [];
          this.applySearchResults(rows, term);
        })
        .finally(() => { this.searching = false; });
    },
    clearSearch() {
      this.searchTerm = '';
      this.searchResults = [];
      this.searchEmpty = false;
      this.showSuggestions = true;
      this.searching = false;
    },
    applySearchResults(rows, term) {
      const arr = Array.isArray(rows) ? rows : [];
      this.searchResults = arr.slice(0, 50);
      this.searchEmpty = this.searchResults.length === 0;
    },
    // opțional: potrivire locală dacă vei vrea fallback
    matchFirma(f, term) {
      if (!f) return false;
      const t = term.toLowerCase();
      if (t.startsWith('id:'))  return String(f.id || '') === t.slice(3).trim();
      if (t.startsWith('cui:')) return String(f.cui || '').toLowerCase().includes(t.slice(4).trim());
      if (t.startsWith('uid:')) return String(f.user_id || '').toLowerCase() === t.slice(4).trim();
      if (t.startsWith('user:')) return String(f.user_name || '').toLowerCase().includes(t.slice(5).trim());
      const hay = [
        String(f.id || ''),
        String(f.denumire || '').toLowerCase(),
        String(f.cui || '').toLowerCase(),
        String(f.user_name || '').toLowerCase(),
        String(f.user_email || '').toLowerCase(),
      ];
      return hay.some(x => x && x.includes(t));
    },
    openFirma(f) {
      this.selectedFirma = f || null;
      this.activeModule = null;
      this.subModule = null;
      this.$emit && this.$emit('select-firma', f);
      // collapse search UI
      this.searchCollapsed = true;
      this.focused = false;
      this.searchResults = [];
      // Navigate to /administrare/work/:id
      if (f && f.id) this.navigate(f.id);
    },
    expandSearch() {
      this.searchCollapsed = false;
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.smart-search__input');
        if (el) { el.focus(); }
      });
    },
    clearSelection() {
      this.selectedFirma = null;
      this.activeModule = null;
      this.subModule = null;
      this.searchCollapsed = false;
      this.clearSearch();
      this.compactModules = false;
      this.compactSubModules = false;

      // navigate to base path (no firma, no module)
      const basePath = this.routePathFor(null, null, null); // '/administrare/work'
      if (this.$route && this.$route.path !== basePath) {
        this.$router.push(basePath).catch(() => {});
      }

      // focus back into search input
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.smart-search__input');
        if (el) { el.focus(); }
      });
    },
    onBlur() {
      // întârziem puțin ca să permită click pe item din listă
      if (this._blurTimer) clearTimeout(this._blurTimer);
      this._blurTimer = setTimeout(() => { this.focused = false; }, 150);
    },
    selectModule(name) {
      if (!this.selectedFirma) return; // guard
      if (this.activeModule === name) {
        // toggle off the module (and any submodule)
        this.activeModule = null;
        this.subModule = null;
        this.compactModules = false;
        this.compactSubModules = false;
        this.navigate(this.selectedFirma.id, null, null);
        return;
      }
      // activate the module
      this.activeModule = name;
      this.subModule = null;
      this.compactModules = true;
      this.compactSubModules = false;
      this.navigate(this.selectedFirma.id, name, null);
    },
    selectSubModule(name) {
      if (!this.selectedFirma) return;
      if (this.subModule === name) {
        // toggle off submodule, stay at module level
        this.subModule = null;
        this.compactSubModules = false;
        const module = this.activeModule || 'facturare';
        this.navigate(this.selectedFirma.id, module, null);
        return;
      }
      // activate submodule
      this.subModule = name;
      this.compactSubModules = true;
      const module = this.activeModule || 'facturare';
      const subroute = (name === 'emise') ? 'facturi_emise' : (name === 'primite' ? 'facturi_primite' : name);
      this.navigate(this.selectedFirma.id, module, subroute);
    },
    onBackClick() {
      if (this.subModule) {
        // If a submodule is active, clear it and go to module level
        const module = this.activeModule || 'facturare';
        this.subModule = null;
        this.compactSubModules = false;
        this.navigate(this.selectedFirma?.id, module, null);
        return;
      }
      if (this.activeModule) {
        // If a module is active, clear it and go to firm root
        this.activeModule = null;
        this.compactModules = false;
        this.navigate(this.selectedFirma?.id, null, null);
        return;
      }
      if (this.selectedFirma) {
        // If a firm is selected, clear selection and go to base work
        this.clearSelection();
        return;
      }
      // fallback to history back if nothing to clear
      if (this.$router && window && window.history && window.history.length > 1) {
        this.$router.go(-1);
      }
    },
    
  },
  created()
  {

  },
}
</script>

<style lang="scss" scoped>
  #kt_docs_search_handler_basic .list-group-item { cursor: pointer; }
  #kt_docs_search_handler_basic .list-group-item:hover { background-color: rgba(0,0,0,0.03); }
  #kt_docs_search_handler_basic .form-control { position: relative; }

  .smart-search { max-width: 100%; }
  .smart-search__box { position: relative; width: 100%; }
  .smart-search__input {
    width: 100%;
    height: 64px;
    border-radius: 16px;
    border: 1px solid rgba(0,0,0,0.08);
    background: #f9fafb;
    padding: 0 60px 0 56px; /* space for icons */
    font-size: 1.125rem; /* 18px */
    transition: box-shadow .2s ease, background .2s ease, border-color .2s ease;
    color: #111;
  }
  .smart-search__input:focus {
    outline: none;
    background: #ffffff;
    box-shadow: 0 6px 24px rgba(0,0,0,0.06);
    border-color: rgba(0,0,0,0.12);
  }

  /* Theme-aware colors for search input (light vs dark) */
  .smart-search__input::placeholder {
    color: rgba(0,0,0,.55);
  }
  [data-bs-theme="dark"] .smart-search__input {
    color: #f1f3f5 !important;
    background: #1f1f1f;
    border-color: rgba(255,255,255,.15);
  }
  [data-bs-theme="dark"] .smart-search__input:focus {
    background: #151515;
    border-color: rgba(255,255,255,.2);
    box-shadow: 0 6px 24px rgba(0,0,0,0.6);
  }
  [data-bs-theme="dark"] .smart-search__input::placeholder {
    color: rgba(255,255,255,.6);
  }
  .smart-search__icon {
    position: absolute; left: 18px; top: 50%; transform: translateY(-50%);
    width: 22px; height: 22px; opacity: .6;
  }
  .smart-search__icon::before {
    content: '';
    display: block;
    width: 22px; height: 22px;
    border: 2px solid currentColor; border-radius: 50%;
    color: #5e6278; opacity: .7; position: relative;
  }
  .smart-search__icon::after {
    content: '';
    position: absolute; left: 15px; top: 15px;
    width: 9px; height: 2px; background: currentColor;
    transform: rotate(45deg);
  }
  .smart-search__clear {
    position: absolute; right: 16px; top: 50%; transform: translateY(-50%);
    width: 34px; height: 34px; border-radius: 50%;
    border: 0; background: #eef2f7; color: #1e1e2d; opacity: .85;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 18px; line-height: 1;
  }
  .smart-search__clear:hover { opacity: 1; background: #e8edf5; }
  .smart-search__spinner { position: absolute; right: 60px; top: 50%; transform: translateY(-50%); width: 20px; height: 20px; border: 2px solid #d1d5db; border-top-color: #5e81f4; border-radius: 50%; animation: smart-spin 1s linear infinite; }
  @keyframes smart-spin { to { transform: translateY(-50%) rotate(360deg); } }

  .smart-results {
    position: absolute; left: 0; right: 0; top: calc(100% + 10px);
    background: #fff; border: 1px solid rgba(0,0,0,0.06); border-radius: 14px;
    box-shadow: 0 14px 40px rgba(0,0,0,0.08);
    max-height: 60vh; overflow: auto; z-index: 50;
  }
  .smart-results__header { padding: 10px 14px; font-weight: 600; color: #5e6278; border-bottom: 1px solid rgba(0,0,0,0.05); }
  .smart-results__list { list-style: none; padding: 0; margin: 0; }
  .smart-results__item { padding: 10px 14px; cursor: pointer; }
  .smart-results__item:hover { background: #f9fafb; }
  .smart-results__empty { padding: 18px 14px; color: #7e8299; text-align: center; }

  .btn.btn-icon { width: 42px; height: 42px; }

  .tile-btn {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 140px;
    height: 110px;
    border-radius: 16px;
    border: 1px solid rgba(0,0,0,0.06);
    background: #fff;
    color: #1e1e2d;
    box-shadow: 0 6px 20px rgba(0,0,0,0.04);
    transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
  }
  .tile-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 26px rgba(0,0,0,0.06);
    border-color: rgba(0,0,0,0.1);
  }
  .tile-btn span { font-weight: 600; }
  .tile-btn--active {
    border-color: #5e81f4;
    box-shadow: 0 10px 26px rgba(94,129,244,0.2);
  }

  .table-row-bordered > :not(caption) > * > * { border-bottom-width: 1px; }
/* Compact tiles when a module/submodule is selected */
.tile-btn i { font-size: 2rem; }

.tiles-compact .tile-btn {
  width: 64px;
  height: 64px;
  gap: 0;
  padding: 0;
}
.tiles-compact .tile-btn i { font-size: 1.5rem !important; }
.tiles-compact .tile-btn span { display: none; }

/* Edge-to-edge table area */
.edge-to-edge {
  /* pull the content to the edges as much as possible inside container-fluid */
  margin-left: -12px;
  margin-right: -12px;
}
@media (min-width: 992px) {
  .edge-to-edge { margin-left: -24px; margin-right: -24px; }
}
@media (min-width: 1400px) {
  .edge-to-edge { margin-left: -32px; margin-right: -32px; }
}


</style>



